import {
  DnsOutlined,
  MoreVert,
  RefreshOutlined,
  VerifiedOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import WhitelabelDetail from "./WhitelabelDetail";
import { hostname } from "../../utils";
import DNSSetupDialog from "./DNSSetupDialog";
import WhitelabelEditorDialog, { WhitelabelInstance } from "./WhitelabelEditorDialog";
import WhitelabelOwnersDialog from "./WhitelabelOwnersDialog";


interface WhitelabelListResponse {
  items: WhitelabelInstance[];
  total: number;
  page: number;
  pageSize: number;
  whitelabelEnabled: boolean;
  whitelabelLimit: number;
  whitelabelUsed: number;
  whitelabelAvailable: number;
  canCreateMore: boolean;
}

export default function Whitelabel({
  currentUser,
  getToken,
  mode,
  eventListener,
}: {
  currentUser: User;
  getToken: () => Promise<string>;
  mode: string;
  eventListener?: Function;
}) {
  const [whitelabels, setWhitelabels] = useState<WhitelabelInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedWhitelabel, setSelectedWhitelabel] =
    useState<WhitelabelInstance | null>(null);
  const [ownersOpen, setOwnersOpen] = useState(false);
  const [dnsSetupOpen, setDnsSetupOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [whitelabelLimit, setWhitelabelLimit] = useState(0);
  const [whitelabelUsed, setWhitelabelUsed] = useState(0);
  const [whitelabelAvailable, setWhitelabelAvailable] = useState(0);
  const [canCreateMore, setCanCreateMore] = useState(false);
  const navigate = useNavigate();
  const [selectedDetailId, setSelectedDetailId] = useState<string | null>(null);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleManageCustomers = (whitelabelId: string) => {
    setSelectedDetailId(whitelabelId);
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleViewAnalytics = (whitelabelId: string) => {
    setSelectedDetailId(whitelabelId);
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleWhitelabelDetails = (whitelabelId: string) => {
    setSelectedDetailId(whitelabelId);
  };

  const deleteWhitelabel = async (whitelabelId: string) => {
    const currentToken = await getToken();
    setLoading(true);

    try {
      await axios.delete(`${hostname}/whitelabel/${whitelabelId}`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      await refreshWhitelabels();
    } catch (err) {
      console.error("Error deleting whitelabel:", err);
    } finally {
      setLoading(false);
    }
  };

  const refreshWhitelabels = async () => {
    setRefreshing(true);
    const currentToken = await getToken();

    try {
      const response = await axios.get(`${hostname}/whitelabel/list`, {
        params: { page: page + 1, pageSize },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      const data = response.data as WhitelabelListResponse;
      setWhitelabels(data.items || []);
      setTotal(data.total || 0);
      setWhitelabelLimit(data.whitelabelLimit || 0);
      setWhitelabelUsed(data.whitelabelUsed || 0);
      setWhitelabelAvailable(data.whitelabelAvailable || 0);
      setCanCreateMore(data.canCreateMore || false);
    } catch (err) {
      console.error("Error loading whitelabels:", err);
    } finally {
      setRefreshing(false);
    }
  };

  const loadWhitelabels = async () => {
    setLoading(true);
    await refreshWhitelabels();
    setLoading(false);
  };

  useEffect(() => {
    if (currentUser.uid) {
      loadWhitelabels();
    }
  }, [currentUser.uid, page, pageSize]);

  // Show detail view if a whitelabel is selected
  if (selectedDetailId) {
    return (
      <WhitelabelDetail
        currentUser={currentUser}
        getToken={getToken}
        mode={mode}
        whitelabelId={selectedDetailId}
        eventListener={eventListener}
        onBack={() => {
          setSelectedDetailId(null);
          refreshWhitelabels();
        }}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="info" sx={{ mb: 3 }}>
        Whitelabel allows you to customize your application's branding and
        share your configuration with other users.
            {whitelabelLimit > 0 && (
              <Box sx={{ mt: 1 }}>
                <strong>Your Plan:</strong> You can create up to {whitelabelLimit}{" "}
                white-label
                {whitelabelLimit > 1 ? "s" : ""}. (Using {whitelabelUsed} of{" "}
                {whitelabelLimit})
              </Box>
            )}
          </Alert>

        <Box>
          <Button
            variant="contained"
            onClick={() => {
              setSelectedWhitelabel(null);
              setEditorOpen(true);
            }}
            startIcon={<VerifiedOutlined />}
            disabled={!canCreateMore}
          >
            Create White-Label
          </Button>
        </Box>

        <Paper sx={{ mt: 3, mb: 5 }} variant="outlined">
          {refreshing && <LinearProgress />}
          {!refreshing && <div style={{ height: 4 }}></div>}

          <IconButton
            onClick={() => refreshWhitelabels()}
            sx={{ float: "right", mr: 1 }}
          >
            <RefreshOutlined />
          </IconButton>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Domain</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {whitelabels.map((whitelabel) => (
                  <TableRow
                    key={whitelabel.id}
                    onClick={() => handleWhitelabelDetails(whitelabel.id)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <TableCell>
                      {whitelabel.settings?.title || "Untitled"}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {whitelabel.domain || "-"}
                        {whitelabel.domain && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedWhitelabel(whitelabel);
                              setDnsSetupOpen(true);
                            }}
                            title="DNS Setup"
                          >
                            <DnsOutlined fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={whitelabel.is_owner ? "Owner" : "Shared"}
                        size="small"
                        color={whitelabel.is_owner ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      {whitelabel.created_date
                        ? moment(whitelabel.created_date).format(
                            "MMM Do YYYY, h:mm a"
                          )
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {whitelabel.updated_date
                        ? moment(whitelabel.updated_date).format(
                            "MMM Do YYYY, h:mm a"
                          )
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setAnchorEl(e.currentTarget);
                          setSelectedWhitelabel(whitelabel);
                          setSelectedId(whitelabel.id);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={selectedId === whitelabel.id}
                        onClose={(e: any) => {
                          e.stopPropagation();
                          setAnchorEl(null);
                          setSelectedId(null);
                        }}
                      >
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleWhitelabelDetails(whitelabel.id);
                            setAnchorEl(null);
                            setSelectedId(null);
                          }}
                        >
                          View Details
                        </MenuItem>

                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditorOpen(true);
                            setAnchorEl(null);
                            setSelectedId(null);
                          }}
                        >
                          Edit Settings
                        </MenuItem>

                        {whitelabel.domain && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setDnsSetupOpen(true);
                              setAnchorEl(null);
                              setSelectedId(null);
                            }}
                          >
                            DNS Setup
                          </MenuItem>
                        )}

                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleManageCustomers(whitelabel.id);
                          }}
                        >
                          Manage Customers
                        </MenuItem>

                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewAnalytics(whitelabel.id);
                          }}
                        >
                          View Analytics
                        </MenuItem>

                        {whitelabel.is_owner && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setOwnersOpen(true);
                              setAnchorEl(null);
                              setSelectedId(null);
                            }}
                          >
                            Manage Owners
                          </MenuItem>
                        )}

                        {whitelabel.is_owner && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteWhitelabel(whitelabel.id);
                              setAnchorEl(null);
                              setSelectedId(null);
                            }}
                          >
                            Delete
                          </MenuItem>
                        )}
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))}

                {whitelabels.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No whitelabel instances found. Create one to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {total > pageSize && (
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={total}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </TableContainer>
        </Paper>

      <WhitelabelEditorDialog
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setSelectedWhitelabel(null);
          refreshWhitelabels();
        }}
        getToken={getToken}
        mode={mode}
        whitelabel={selectedWhitelabel}
        eventListener={eventListener}
      />

      <WhitelabelOwnersDialog
        open={ownersOpen}
        onClose={() => {
          setOwnersOpen(false);
          setSelectedWhitelabel(null);
        }}
        getToken={getToken}
        whitelabel={selectedWhitelabel}
        eventListener={eventListener}
      />

      <DNSSetupDialog
        open={dnsSetupOpen}
        onClose={() => {
          setDnsSetupOpen(false);
          setSelectedWhitelabel(null);
        }}
        whitelabel={selectedWhitelabel}
        getToken={getToken}
      />
    </Box>
  );
}
